import React from "react";
import { Users, ArrowClockwise, Fingerprint as FingerprintIcon } from "@phosphor-icons/react";
import { TID } from "@/constants/testIds";

/**
 * Portfolio Diversity Score — proof that Fingerprint Variance + Cluster
 * Rotation are actually diversifying the user's build.
 */
export default function DiversityCard({ diversity, onRefresh }) {
  if (!diversity) return null;

  const gradeColor = {
    ELITE: "text-[#39FF14] border-[#39FF14]",
    STRONG: "text-[#39FF14] border-[#39FF14]",
    MODERATE: "text-[#FFCC00] border-[#FFCC00]",
    LOW: "text-[#FF3B30] border-[#FF3B30]",
    "VERY LOW": "text-[#FF3B30] border-[#FF3B30]",
  }[diversity.grade] || "text-zinc-400 border-zinc-700";

  const scoreColor = diversity.score >= 70 ? "#39FF14" : diversity.score >= 55 ? "#FFCC00" : "#FF3B30";

  return (
    <section
      data-testid={TID.diversityCard}
      className="bg-[#121212] border border-zinc-800"
    >
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <FingerprintIcon size={20} weight="fill" className="text-[#39FF14]" />
          <div>
            <div className="overline">Portfolio Diversity Score</div>
            <div className="text-xs text-zinc-400">Proof your portfolio is uniquely-yours</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[10px] mono px-2 py-0.5 border font-bold tracking-widest ${gradeColor}`}>
            {diversity.grade}
          </span>
          {onRefresh && (
            <button
              data-testid={TID.diversityRefreshBtn}
              onClick={onRefresh}
              className="text-[10px] mono uppercase tracking-widest border border-zinc-800 hover:border-[#39FF14] hover:text-[#39FF14] text-zinc-400 px-2 py-1 transition-colors"
            >
              <ArrowClockwise size={10} weight="bold" className="inline mr-1" />
              Refresh
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-5">
        {/* Big Score */}
        <div className="lg:col-span-3 flex flex-col items-center justify-center bg-black border border-zinc-800 py-6">
          <div
            data-testid={TID.diversityScore}
            className="heading font-black mono text-7xl leading-none"
            style={{ color: scoreColor }}
          >
            {diversity.score}
          </div>
          <div className="overline mt-2 text-zinc-500">DIVERSITY / 100</div>
        </div>

        {/* Metrics */}
        <div className="lg:col-span-4 grid grid-cols-2 gap-3">
          <MiniStat label="Lineups" value={diversity.portfolio_size} />
          <MiniStat label="Unique Players" value={diversity.unique_players} />
          <MiniStat label="Unique %" value={`${diversity.unique_pct}%`} accent={diversity.unique_pct >= 20} />
          <MiniStat
            label="Avg Overlap"
            value={`${diversity.avg_pairwise_overlap}%`}
            warn={diversity.avg_pairwise_overlap > 60}
          />
          <MiniStat
            label="Max Overlap"
            value={`${diversity.max_pairwise_overlap}%`}
            warn={diversity.max_pairwise_overlap > 78}
          />
          <MiniStat label="Total Slots" value={diversity.total_slots} />
        </div>

        {/* Distribution chart */}
        <div className="lg:col-span-5">
          <div className="overline mb-2">Overlap Distribution (pairs)</div>
          <div className="space-y-1">
            {diversity.distribution.map((bucket, i) => {
              const maxCount = Math.max(...diversity.distribution.map((b) => b.count), 1);
              const pct = (bucket.count / maxCount) * 100;
              return (
                <div key={bucket.bucket} className="flex items-center gap-2 text-[10px] mono">
                  <div className="w-16 text-zinc-500 text-right">{bucket.bucket}</div>
                  <div className="flex-1 bg-black border border-zinc-900 h-3 relative overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${pct}%`,
                        background: i < 3 ? "#39FF14" : i < 6 ? "#FFCC00" : "#FF3B30",
                        opacity: bucket.count > 0 ? 0.8 : 0,
                      }}
                    />
                  </div>
                  <div className="w-8 text-right text-zinc-400">{bucket.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top shared pairs */}
      {diversity.top_shared_pairs?.length > 0 && (
        <div className="border-t border-zinc-800 p-5">
          <div className="overline mb-2 flex items-center gap-2">
            <Users size={12} /> Most-Shared Player Combos (over-clustering signals)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {diversity.top_shared_pairs.map((p) => (
              <div key={`${p.player_a}|${p.player_b}`} className="flex items-center justify-between bg-black border border-zinc-900 px-3 py-2 text-xs mono">
                <span className="truncate">
                  <span className="text-white">{p.player_a}</span>
                  <span className="text-zinc-500 mx-1">+</span>
                  <span className="text-white">{p.player_b}</span>
                </span>
                <span className={`ml-3 ${p.pct > 60 ? "text-[#FF3B30]" : p.pct > 40 ? "text-[#FFCC00]" : "text-zinc-400"}`}>
                  {p.count}× · {p.pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function MiniStat({ label, value, accent, warn }) {
  const color = accent ? "text-[#39FF14]" : warn ? "text-[#FF3B30]" : "text-white";
  return (
    <div className="bg-black border border-zinc-800 px-3 py-2">
      <div className="overline text-[9px]">{label}</div>
      <div className={`heading text-xl font-black mono mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
